/**
 * When to sync, and what happens when one runs.
 *
 * Push on change, pull on open and on return to foreground, flush on the
 * browser's `online` event. No Realtime and no websocket.
 *
 * There is NO outbox queue. "Pending" is derived: an item is unsynced exactly
 * when its `updatedAt` is newer than the week's `lastSyncedAt`. That is
 * stateless and self-healing — a failed push simply does not advance
 * `lastSyncedAt`, so the same items are retried next time — and there is no
 * queue that can itself be lost, corrupted, or drift out of step with the data
 * it describes.
 */

import {
  loadLibrary, loadPlan, loadSettings, savePlan, saveLibrary, saveSettings,
  saveSyncState, withItemMeta, withLibraryMeta, type StoredPlan,
} from '../store/db.js';
import { DEFAULT_STAPLES, type Library } from '../store/library.js';
import type { Settings } from '../store/settings.js';
import { clampFuture, mergeItems, type SyncItem } from './merge.js';
import {
  itemsToLibrary, itemsToPlan, libraryToItems, planToItems,
} from './planItems.js';
import {
  currentSession, fetchRemote, fetchSettingsRow, householdId, isConfigured, pushItems,
  pushSettingsRow, serverNowMs,
} from './client.js';
import { deviceId } from './device.js';

export type SyncStatus =
  | { state: 'off' }            // not configured, or signed out
  | { state: 'syncing' }
  | { state: 'ok'; at: string }
  | { state: 'error'; message: string };

export interface SyncResult {
  status: SyncStatus;
  /** Present when the merge changed what is stored locally. */
  plan?: StoredPlan;
  library?: Library;
  settings?: Settings;
}

/** True when sharing is switched on: configured AND signed in. */
export async function sharingActive(): Promise<boolean> {
  if (!isConfigured()) return false;
  return (await currentSession()) !== null;
}

/**
 * One full reconcile of a single week, plus the library and settings.
 *
 * The week is the unit because a week is only a few dozen rows — small enough
 * that reconciling the whole thing is simpler, and far more robust, than
 * maintaining per-row change cursors that can themselves go stale.
 */
export async function syncWeek(weekStart: string): Promise<SyncResult> {
  if (!(await sharingActive())) return { status: { state: 'off' } };

  const household = await householdId();
  if (!household) {
    // Signed in, but the account is not linked to a household. That is a setup
    // problem, not a transient one, so it is worth saying out loud rather than
    // retrying silently forever.
    return { status: { state: 'error', message: 'This account is not linked to a household yet.' } };
  }

  try {
    const [storedPlan, storedLibrary, settings] = await Promise.all([
      loadPlan(weekStart), loadLibrary(), loadSettings(),
    ]);

    const localPlan = storedPlan ?? null;
    const localPlanItems = localPlan ? planToItems(withItemMeta(localPlan)) : [];
    const localLibraryItems = libraryToItems(withLibraryMeta(storedLibrary));

    // PULL BEFORE PUSH, and push only what the merge actually chose.
    //
    // The obvious order — push our pending edits, then pull — loses data. An
    // upsert overwrites whatever row is there, so pushing a locally-pending
    // item that the other phone has since edited MORE recently clobbers their
    // newer value; the pull then hands us back our own stale copy and their
    // edit is gone for good. Deciding first, and sending only winners, means
    // nothing that is already newer on the server can be overwritten.
    const serverNow = await serverNowMs();
    const remote = await fetchRemote(household, weekStart);

    const remotePlan = clampFuture(remote.plan, serverNow);
    const remoteLibrary = clampFuture(remote.library, serverNow);

    const mergedPlanItems = mergeItems(localPlanItems, remotePlan);
    const mergedLibraryItems = mergeItems(localLibraryItems, remoteLibrary);

    const toPush = [
      ...winnersNotOn(mergedPlanItems, remotePlan),
      ...winnersNotOn(mergedLibraryItems, remoteLibrary),
    ];
    if (toPush.length > 0) {
      const pushed = await pushItems(household, weekStart, toPush);
      // A failed push is not a failed sync: the merge is still correct locally,
      // and `lastSyncedAt` is left where it was so these items are retried.
      if (pushed.error) {
        // Library before plan here too, or this path re-opens the gap where a
        // plan on disk names a recipe that is not.
        await saveLibrary(mergedLibraryFrom(mergedLibraryItems, storedLibrary));
        await savePlanIfAny(weekStart, localPlan, settings, mergedPlanItems);
        return { status: { state: 'error', message: pushed.error } };
      }
    }

    const result: SyncResult = { status: { state: 'ok', at: new Date(serverNow).toISOString() } };

    // The library first: a plan naming a recipe should never be on disk
    // before the recipe itself. The UI tolerates the gap (withoutUnresolved),
    // but there is no reason to open it.
    const mergedLibrary = mergedLibraryFrom(mergedLibraryItems, storedLibrary);
    await saveLibrary(mergedLibrary);
    result.library = mergedLibrary;

    if (mergedPlanItems.length > 0) {
      const merged = itemsToPlan(weekStart, localPlan?.store ?? settings.defaultStore, mergedPlanItems);
      await savePlan(merged);
      result.plan = merged;
    }

    const mergedSettings = await syncSettings(household, settings);
    if (mergedSettings) {
      await saveSettings(mergedSettings);
      result.settings = mergedSettings;
    }

    await saveSyncState({ weekStart, lastSyncedAt: new Date(serverNow).toISOString() });
    return result;
  } catch (error: unknown) {
    return {
      status: { state: 'error', message: error instanceof Error ? error.message : 'Sync failed.' },
    };
  }
}

/**
 * The merged items the server does not already have in exactly that form.
 *
 * Sending only these keeps a sync to the rows that actually changed, and — more
 * importantly — guarantees every row sent is the newest version known to
 * anyone, so an upsert can never move a row backwards.
 */
function winnersNotOn(merged: SyncItem[], remote: SyncItem[]): SyncItem[] {
  const byKey = new Map(remote.map((i) => [i.key, i]));
  return merged.filter((item) => {
    const theirs = byKey.get(item.key);
    if (!theirs) return true;
    return theirs.meta.updatedAt !== item.meta.updatedAt
      || theirs.meta.updatedBy !== item.meta.updatedBy
      || (theirs.meta.deleted === true) !== (item.meta.deleted === true);
  });
}

/** The merged library, keeping the standing staples when the account has none yet. */
function mergedLibraryFrom(items: SyncItem[], stored: Library): Library {
  return itemsToLibrary(items, stored.staples.length > 0 ? stored.staples : DEFAULT_STAPLES);
}

/** Persist a merged plan even on a failed push — the merge itself is sound. */
async function savePlanIfAny(
  weekStart: string,
  localPlan: StoredPlan | null,
  settings: Settings,
  items: SyncItem[],
): Promise<void> {
  if (items.length === 0) return;
  await savePlan(itemsToPlan(weekStart, localPlan?.store ?? settings.defaultStore, items));
}

/**
 * Settings are whole-object last-write-wins, the one deliberate exception to
 * per-item merge. Returns the settings to store locally, or null if local
 * already wins.
 */
async function syncSettings(household: string, local: Settings): Promise<Settings | null> {
  const row = await fetchSettingsRow(household);
  const localAt = Date.parse(local.updatedAt ?? '');
  const remoteAt = row ? Date.parse(String(row['updated_at'] ?? '')) : NaN;

  const localWins = !Number.isNaN(localAt) && (Number.isNaN(remoteAt) || localAt > remoteAt);

  if (localWins || !row) {
    // Only push settings that have actually been edited. An untouched device
    // still holds DEFAULT_SETTINGS with no timestamp, and pushing those would
    // overwrite the other phone's real household with the defaults.
    if (!Number.isNaN(localAt)) {
      await pushSettingsRow(household, {
        people: local.people,
        effort: local.effort,
        default_store: local.defaultStore,
        updated_at: local.updatedAt,
        updated_by: local.updatedBy ?? deviceId(),
      });
    }
    return null;
  }

  return {
    people: row['people'] as Settings['people'],
    effort: row['effort'] as Settings['effort'],
    defaultStore: String(row['default_store']) as Settings['defaultStore'],
    updatedAt: new Date(String(row['updated_at'])).toISOString(),
    updatedBy: String(row['updated_by'] ?? ''),
  };
}

/**
 * Wire the triggers. Returns a teardown function.
 *
 * `visibilitychange` rather than `focus`: coming back to an installed PWA from
 * the home screen fires the former reliably and the latter does not.
 */
export function startSyncTriggers(run: () => void): () => void {
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') run();
  };
  document.addEventListener('visibilitychange', onVisible);
  globalThis.addEventListener?.('online', run);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    globalThis.removeEventListener?.('online', run);
  };
}

/**
 * Collapse a burst of edits into one push.
 *
 * Ticking six things in a shop is six state changes in as many seconds. Without
 * this, that is six round trips on a connection that may barely exist.
 */
export function debounce(fn: () => void, ms: number): (() => void) & { cancel(): void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const wrapped = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, ms);
  };
  wrapped.cancel = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return wrapped;
}

export function itemCount(items: SyncItem[]): number {
  return items.filter((i) => i.value !== null).length;
}
